import React, {CSSProperties, useState} from "react";
import {Dialog} from "@components/interface/Dialog";
import {connect} from "react-redux";
import {selectDialog} from "@redux/selectors/dialog";
import {IDialog, update as updateDialog} from "@redux/slices/dialog";
import {Update} from "@redux/slices/base";
import {COLOURS, THEME} from "@components/core/theme";

// Bonus colour for tier-1 bonus tiles — matches BONUS_COLOURS[1] in PlaceView.tsx
const BONUS_COLOUR_1 = "#8b6914";

// Inline ShovelIcon paths (matches ShovelIcon.tsx viewBox 0 0 32 32) for use inside <svg> transforms
interface IInlineShovelPaths { color?: string; background?: string }
const InlineShovelPaths = ({color = "white", background = "white"}: IInlineShovelPaths) => (
    <>
        <line x1="13.5" y1="3" x2="18.5" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <line x1="16" y1="3" x2="16" y2="20" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        <rect x="14" y="19" width="4" height="2.5" rx={0.8} fill={color}/>
        <path d="M11 22 Q11 30.5 16 32 Q21 30.5 21 22 Z" fill={color}/>
        <line x1="16" y1="23" x2="16" y2="29.5" stroke={background} strokeWidth="0.5" strokeLinecap="round"/>
        <path d="M8 30 L9.5 29.4 L11 30.2" stroke={color} strokeWidth="1.3" strokeLinecap="round"
              strokeLinejoin="round" fill="none" opacity={0.7}/>
        <path d="M21 30.2 L22.5 29.4 L24 30" stroke={color} strokeWidth="1.3" strokeLinecap="round"
              strokeLinejoin="round" fill="none" opacity={0.7}/>
    </>
);

const styles: Record<string, CSSProperties> = {
    ...THEME,
    body: {
        color: COLOURS.ink,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontSize: "medium",
        lineHeight: 1.5,
        marginTop: 8,
        textAlign: "center"
    },
    dots: {
        display: "flex",
        justifyContent: "center",
        gap: 8,
        marginTop: 16
    },
    dot: {
        borderRadius: "50%",
        height: 10,
        width: 10,
        cursor: "pointer"
    },
    nav: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: 16
    },
    slide: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexGrow: 1,
        padding: "0 8px"
    },
    stepHeading: {
        color: COLOURS.forestGreen,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontSize: "large",
        fontWeight: "bold",
        marginTop: 12,
        textAlign: "center"
    }
};

const svgStyle: CSSProperties = {margin: "16px auto", display: "block"};

const WelcomeSvg = () => {
    const letters: [string, number, number][] = [["W",0,0],["O",1,0],["R",2,0],["D",3,0]];
    return (
        <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
            <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>
            {[0,1,2,3,4].flatMap(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={10 + c*22} y={10 + r*22} width={18} height={18}
                      rx="2" fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1"/>
            )))}
            {letters.map(([l, c, r]) => (
                <text key={`${c}-${r}`} x={19 + c*22} y={23 + r*22}
                      textAnchor="middle" fontSize="11" fontFamily="monospace"
                      fontWeight="bold" fill={COLOURS.forestGreen}>{l}</text>
            ))}
            <circle cx="96" cy="55" r="16" fill={COLOURS.forestGreen} opacity="0.12"
                    stroke={COLOURS.forestGreen} strokeWidth="1.5"/>
            <text x="96" y="61" textAnchor="middle" fontSize="18">🗺️</text>
        </svg>
    );
};

const PlacingSvg = () => {
    const word: [string, number, number][] = [["C",0,0],["A",1,0],["T",2,0]];
    const hand = ["A","B","C"];
    return (
        <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
            <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>
            {[0,1,2].flatMap(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={8 + c*22} y={8 + r*22} width={18} height={18}
                      rx="2" fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1"/>
            )))}
            {word.map(([, c, r]) => (
                <rect key={`hl${c}${r}`} x={8 + c*22} y={8 + r*22}
                      width={18} height={18} rx="2" fill={COLOURS.forestGreen}
                      opacity="0.25" stroke={COLOURS.forestGreen} strokeWidth="1.5"/>
            ))}
            {word.map(([l, c, r]) => (
                <text key={`t${c}${r}`} x={17 + c*22} y={21 + r*22}
                      textAnchor="middle" fontSize="11" fontFamily="monospace"
                      fontWeight="bold" fill={COLOURS.forestGreen}>{l}</text>
            ))}
            {hand.map((l, i) => (
                <rect key={`h${i}`} x={72 + i*16} y={52} width={14} height={20}
                      rx="3" fill={COLOURS.amber} opacity="0.7" stroke={COLOURS.agedBorder}/>
            ))}
            {hand.map((l, i) => (
                <text key={`ht${i}`} x={79 + i*16} y={66} textAnchor="middle"
                      fontSize="10" fontFamily="monospace" fontWeight="bold"
                      fill={COLOURS.parchment}>{l}</text>
            ))}
            <path d="M74 50 L40 28" stroke={COLOURS.forestGreen} strokeWidth="1.5" strokeDasharray="3,2"/>
            <polygon points="40,28 46,30 43,36" fill={COLOURS.forestGreen}/>
        </svg>
    );
};

const ScoringSvg = () => {
    const word: [string, number][] = [["Q",0],["U",1],["I",2],["Z",3]];
    return (
        <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
            <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>
            {word.map(([, i]) => (
                <rect key={i} x={8 + i*22} y={20} width={18} height={18}
                      rx="2" fill={COLOURS.forestGreen} opacity="0.2" stroke={COLOURS.forestGreen}/>
            ))}
            {word.map(([l, i]) => (
                <text key={`t${i}`} x={17 + i*22} y={33} textAnchor="middle"
                      fontSize="11" fontFamily="monospace" fontWeight="bold"
                      fill={COLOURS.forestGreen}>{l}</text>
            ))}
            <circle cx="95" cy="42" r="22" fill={COLOURS.amber} opacity="0.12"
                    stroke={COLOURS.amber} strokeWidth="1.5"/>
            <text x="95" y="38" textAnchor="middle" fontSize="9"
                  fontFamily="Georgia, serif" fill={COLOURS.amber}>score</text>
            <text x="95" y="52" textAnchor="middle" fontSize="16"
                  fontFamily="Georgia, serif" fontWeight="bold" fill={COLOURS.amber}>+42</text>
            <text x="44" y="62" textAnchor="middle" fontSize="8"
                  fontFamily="Georgia, serif" fill={COLOURS.ink} opacity="0.7">rarity × new tiles</text>
        </svg>
    );
};

// Earning Shovels: shows a bonus tile (amber bg, dotted white border, shovel icon, +1 label)
// flanked by letter tiles, with an arrow to an earned-shovel indicator on the right.
const EarningShovelsSvg = () => (
    <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
        <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>

        {/* Letter tile C */}
        <rect x="4" y="23" width="18" height="18" rx="2"
              fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1"/>
        <text x="13" y="35" textAnchor="middle" fontSize="11" fontFamily="monospace"
              fontWeight="bold" fill={COLOURS.ink}>C</text>

        {/* Bonus tile — matches PlaceView: amber bg, dotted white border, shovel icon, +1 label */}
        <rect x="24" y="16" width="28" height="28" rx="2"
              fill={BONUS_COLOUR_1} stroke="white" strokeWidth="1.5" strokeDasharray="2.5,1.5"/>
        {/* Shovel icon scaled to ~22×22 within the 28×28 tile */}
        <g transform="translate(27, 19) scale(0.6875)">
            <InlineShovelPaths color="white" background={BONUS_COLOUR_1}/>
        </g>
        {/* +1 label — bottom-right corner, matching PlaceView shovelText position */}
        <text x="50" y="42" textAnchor="end" fontSize="7" fontFamily="Georgia, serif"
              fontWeight="bold" fill="white">+1</text>

        {/* Letter tile T */}
        <rect x="54" y="23" width="18" height="18" rx="2"
              fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1"/>
        <text x="63" y="35" textAnchor="middle" fontSize="11" fontFamily="monospace"
              fontWeight="bold" fill={COLOURS.ink}>T</text>

        {/* Arrow to earned-shovel indicator */}
        <path d="M74 30 L84 30" stroke={COLOURS.amber} strokeWidth="1.5"/>
        <polygon points="84,27.5 90,30 84,32.5" fill={COLOURS.amber}/>

        {/* Earned shovel: amber circle + shovel icon in amber */}
        <circle cx="104" cy="30" r="14" fill={BONUS_COLOUR_1} opacity="0.16"
                stroke={BONUS_COLOUR_1} strokeWidth="1.2"/>
        {/* Shovel scaled to ~20×20, centred in circle */}
        <g transform="translate(94, 20) scale(0.625)">
            <InlineShovelPaths color={BONUS_COLOUR_1} background={COLOURS.parchmentDark}/>
        </g>
    </svg>
);

// Using Shovels: shows the game's shovel icon prominently, with an arrow to a regular
// (non-bonus) letter tile, indicating the letter is dug up and returned to the hand.
const UsingShovelsSvg = () => (
    <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
        <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>

        {/* Decorative circle behind shovel */}
        <circle cx="34" cy="34" r="26" fill={COLOURS.forestGreen} opacity="0.12"
                stroke={COLOURS.forestGreen} strokeWidth="1.2"/>

        {/* Shovel icon — 32×32 native scale, matching ShovelIcon.tsx exactly */}
        <g transform="translate(18, 10) scale(1.0)">
            <InlineShovelPaths color={COLOURS.forestGreen} background={COLOURS.parchmentDark}/>
        </g>

        {/* Arrow pointing at the target tile */}
        <path d="M62 36 L73 36" stroke={COLOURS.forestGreen} strokeWidth="2"/>
        <polygon points="73,33 79,36 73,39" fill={COLOURS.forestGreen}/>

        {/* Regular (non-bonus) letter tile being dug up */}
        <rect x="81" y="22" width="24" height="24" rx="2"
              fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1.5"/>
        <text x="93" y="39" textAnchor="middle" fontSize="15" fontFamily="monospace"
              fontWeight="bold" fill={COLOURS.ink}>M</text>

        {/* Upward arrow indicating the letter returns to the player's hand */}
        <path d="M93 21 L93 12" stroke={COLOURS.amber} strokeWidth="1.5"/>
        <polygon points="90,14 93,8 96,14" fill={COLOURS.amber}/>
        <text x="93" y="72" textAnchor="middle" fontSize="7" fontFamily="Georgia, serif"
              fill={COLOURS.ink} opacity="0.7">goes to your hand</text>
    </svg>
);

const NavigationSvg = () => {
    const words = ["QUARTZ","JIVING","FOXY"];
    return (
        <svg style={svgStyle} width="120" height="80" viewBox="0 0 120 80">
            <rect x="0" y="0" width="120" height="80" rx="6" fill={COLOURS.parchmentDark}/>
            <circle cx="30" cy="40" r="22" fill={COLOURS.parchment} stroke={COLOURS.agedBorder} strokeWidth="1.5"/>
            <polygon points="30,22 34,38 30,35 26,38" fill={COLOURS.forestGreen}/>
            <polygon points="30,58 34,42 30,45 26,42" fill={COLOURS.agedBorder}/>
            <polygon points="48,40 32,36 35,40 32,44" fill={COLOURS.agedBorder}/>
            <polygon points="12,40 28,36 25,40 28,44" fill={COLOURS.agedBorder}/>
            <text x="30" y="20" textAnchor="middle" fontSize="8"
                  fontFamily="Georgia, serif" fontWeight="bold" fill={COLOURS.forestGreen}>N</text>
            <rect x="68" y="12" width="46" height="56" rx="4"
                  fill={COLOURS.parchment} stroke={COLOURS.agedBorder}/>
            <text x="91" y="24" textAnchor="middle" fontSize="8"
                  fontFamily="Georgia, serif" fontWeight="bold" fill={COLOURS.forestGreen}>Top Words</text>
            {words.map((w, i) => (
                <text key={w} x="73" y={35 + i*13} fontSize="7"
                      fontFamily="monospace" fill={COLOURS.ink}>{`${i+1}. ${w}`}</text>
            ))}
        </svg>
    );
};

interface ISlide {
    heading: string
    body: string
    illustration: React.ReactElement
}

const SLIDES: ISlide[] = [
    {
        heading: "Welcome to Wordland!",
        body: "Wordland is a living map covered in words. Explore the world, place letters, and compete with other players to build the highest-scoring words.",
        illustration: <WelcomeSvg/>
    },
    {
        heading: "Placing Letters",
        body: "Drag letters from your hand onto the map. Place them adjacent to existing tiles to form a word, then tap Go to score it.",
        illustration: <PlacingSvg/>
    },
    {
        heading: "Scoring Words",
        body: "Your score is based on word rarity — uncommon words score higher. Placing new letters adds a bonus. Repeating the same word earns less each time.",
        illustration: <ScoringSvg/>
    },
    {
        heading: "Earning Shovels",
        body: "Placing words on bonus tiles earns you shovels.",
        illustration: <EarningShovelsSvg/>
    },
    {
        heading: "Using Shovels",
        body: "Use a shovel to dig up any existing letter on the map that is not on a bonus tile and place it into your hand.",
        illustration: <UsingShovelsSvg/>
    },
    {
        heading: "Navigation",
        body: "Pan and zoom to explore the map. The compass points toward nearby players. Check the leaderboard to find the highest-scoring words and visit them on the map.",
        illustration: <NavigationSvg/>
    }
];

interface ITutorialDialog {
    dialog: IDialog
    updateDialog: Update
}
function TutorialDialog({dialog, updateDialog}: ITutorialDialog) {
    const [step, setStep] = useState(0);

    if (dialog.name !== "tutorial") return null;

    const isFirst = step === 0;
    const isLast = step === SLIDES.length - 1;
    const slide = SLIDES[step];

    const handleClose = () => {
        localStorage.setItem("hasSeenTutorial", "true");
        updateDialog({update: {}});
    };
    const handleNext = () => {
        if (isLast) {
            handleClose();
        } else {
            setStep(s => s + 1);
        }
    };

    return (
        <Dialog title="TutorialMenuTitle">
            <div style={styles.slide}>
                {slide.illustration}
                <div style={styles.stepHeading}>{slide.heading}</div>
                <p style={styles.body}>{slide.body}</p>
            </div>
            <div style={styles.dots}>
                {SLIDES.map((_, i) => (
                    <div
                        key={i}
                        onClick={() => setStep(i)}
                        style={{
                            ...styles.dot,
                            backgroundColor: i === step ? COLOURS.forestGreen : COLOURS.agedBorder
                        }}
                    />
                ))}
            </div>
            <div style={styles.nav}>
                <button
                    onClick={() => setStep(s => s - 1)}
                    style={{...styles.button, visibility: isFirst ? "hidden" : "visible"}}
                >
                    ← Back
                </button>
                <button onClick={handleClose} style={styles.button}>
                    Skip
                </button>
                <button onClick={handleNext} style={styles.button}>
                    {isLast ? "Got it!" : "Next →"}
                </button>
            </div>
        </Dialog>
    );
}

const mapStateToProps = (store: never) => ({
    dialog: selectDialog(store)
});
const connected = connect(mapStateToProps, {
    updateDialog
})(TutorialDialog);
export {connected as TutorialDialog}
